import { ReactNode, useEffect, useState } from 'react';
import GoogleAd from './GoogleAd';
import AnchorAd from './AnchorAd';
import { useAdConfig } from '../hooks/useAdConfig';

interface PageWithAdsProps {
  children: ReactNode;
  leftAdSlot?: string;
  rightAdSlot?: string;
}

/**
 * Page layout with Google Ads on sides (desktop only)
 */
export default function PageWithAds({ 
  children, 
  leftAdSlot = '1234567890',
  rightAdSlot = '0987654321'
}: PageWithAdsProps) {
  const { showAdsForCurrentUser } = useAdConfig();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  if (!isDesktop || !showAdsForCurrentUser) {
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "16px" }}>
        {children}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      position: 'relative',
      minHeight: '100vh',
    }}>
      {/* Left Ad - Fixed to left edge */}
      <aside style={{
        position: 'fixed',
        left: '0',
        top: '80px',
        width: '160px',
        height: 'calc(100vh - 80px)',
        zIndex: 10,
      }}>
        <GoogleAd 
          slot={leftAdSlot} 
          format="vertical"
          style={{ minHeight: '600px' }}
        />
      </aside>

      {/* Main Content - Centered with padding for ads */}
      <div style={{ 
        flex: 1,
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '16px',
        width: '100%',
      }}>
        {children}
      </div>

      {/* Right Ad - Fixed to right edge */}
      <aside style={{
        position: 'fixed',
        right: '0',
        top: '80px',
        width: '160px',
        height: 'calc(100vh - 80px)',
        zIndex: 10,
      }}>
        <GoogleAd 
          slot={rightAdSlot} 
          format="vertical"
          style={{ minHeight: '600px' }}
        />
      </aside>

      {/* Anchor Ad - Bottom banner */}
      <AnchorAd slot="ANCHOR_BOTTOM" />
    </div>
  );
}
